import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { SlidersHorizontal, MapPin, X, Map as MapIcon, List as ListIconLucide, ChevronDown, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import MapSearchView from '../components/MapSearchView';
import { PROPERTY_TYPES, formatCOP } from '../mock';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { api } from '../lib/api';
import { track, EventTypes } from '../lib/analytics';
import advisor from '../lib/advisor';
import AdvisorSearchLens from '../components/advisor/AdvisorSearchLens';
import SEO from '../components/SEO';
import { PropertyCardSkeletonGrid } from '../components/Skeletons';

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('reciente');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [advisorReasons, setAdvisorReasons] = useState({}); // itemId → explicación del Advisor (R3)
  const [bbox, setBbox] = useState(null);       // {west,south,east,north} | null
  const [mobileView, setMobileView] = useState('list');  // 'list' | 'map'
  const [hoveredId, setHoveredId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const toggleCompare = (id) => setCompareIds((cur) => {
    if (cur.includes(id)) return cur.filter(x => x !== id);
    if (cur.length >= 3) return cur;
    return [...cur, id];
  });

  const op = params.get('op') || 'venta';
  // Iter 43 — accept `?ciudad=X` as alias for `?q=X` (from home city cards)
  const q = params.get('q') || params.get('ciudad') || '';
  const type = params.get('type') || '';
  const beds = parseInt(params.get('beds') || '0');
  const priceRange = op === 'venta' ? [0, 3000000000] : [0, 10000000];
  const minPrice = parseInt(params.get('minPrice') || priceRange[0]);
  const maxPrice = parseInt(params.get('maxPrice') || priceRange[1]);

  useEffect(() => {
    setLoading(true);
    const apiParams = { op, sort: sortBy, limit: bbox ? 500 : 50 };
    if (q) apiParams.q = q;
    if (type) apiParams.type = type;
    if (beds) apiParams.beds = beds;
    if (minPrice > priceRange[0]) apiParams.minPrice = minPrice;
    if (maxPrice < priceRange[1]) apiParams.maxPrice = maxPrice;
    if (bbox) apiParams.bbox = `${bbox.west},${bbox.south},${bbox.east},${bbox.north}`;

    api.listProperties(apiParams)
      .then(r => {
        setItems(r.items || []);
        setTotal(r.total || 0);
        // GA4 canonical "search" event — fires whenever a search runs
        // (initial load + every filter/sort/map-pan). Only report the
        // non-bbox variants to avoid over-counting map interactions.
        if (!bbox) {
          track(EventTypes.SEARCH, {
            search_term: q || undefined,
            op,
            type: type || undefined,
            beds: beds || undefined,
            sort: sortBy,
            results_count: r.total || 0,
          });
          // Advisor: enriquecer el Context con los filtros y el volumen
          // de resultados. Si el usuario aterrizó directo por URL sin
          // pasar por el SearchBar, iniciamos un Context aquí.
          advisor.getOrStartContext({
            entryPoint: 'search',
            intent: q ? `buscar ${op} en ${q}` : `buscar ${op}`,
            payload: { op, q: q || null, started_from: 'direct_url' },
          }).then(() => {
            advisor.updateContext('search_executed', {
              op, q: q || null, type: type || null, beds: beds || null,
              minPrice: minPrice > priceRange[0] ? minPrice : null,
              maxPrice: maxPrice < priceRange[1] ? maxPrice : null,
              sort: sortBy,
              results_count: r.total || 0,
            });
          });
        }
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [op, q, type, beds, minPrice, maxPrice, sortBy, bbox]);

  // When the user changes any FILTER (not the map bounds), reset bbox so
  // the next fetch is a city-level query that auto-fits the map.
  useEffect(() => { setBbox(null); }, [op, q, type, beds, minPrice, maxPrice]);

  const onMapBoundsConfirmed = useCallback((b) => {
    setBbox(b);
  }, []);

  const updateParam = (key, value) => {
    const np = new URLSearchParams(params);
    if (value === '' || value === null || value === undefined || value === '0') np.delete(key);
    else np.set(key, value);
    setParams(np);
  };

  // Active filter chips (mobile)
  const activeFilters = [];
  if (q) activeFilters.push({ key: 'q', label: q });
  if (type) activeFilters.push({ key: 'type', label: PROPERTY_TYPES.find(t => t.value === type)?.label || type });
  if (beds) activeFilters.push({ key: 'beds', label: `${beds}+ hab` });
  if (minPrice > priceRange[0] || maxPrice < priceRange[1]) {
    activeFilters.push({ key: 'price', label: `${formatCOP(minPrice)} - ${formatCOP(maxPrice)}` });
  }

  const clearPrice = () => {
    const np = new URLSearchParams(params);
    np.delete('minPrice');
    np.delete('maxPrice');
    setParams(np);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${op === 'venta' ? 'Comprar' : 'Arrendar'} ${type ? PROPERTY_TYPES.find(t => t.value === type)?.label.toLowerCase() : 'propiedades'}${q ? ` en ${q}` : ' en Colombia'}`}
        description={`Encuentra ${total} propiedades ${op === 'venta' ? 'en venta' : 'en arriendo'}${q ? ` en ${q}` : ' en Colombia'}. Filtra por precio, habitaciones, tipo y zona.`}
      />
      <Header />

      {/* DESKTOP STICKY FILTERS BAR */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-100 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
            {['venta', 'arriendo'].map(o => (
              <button key={o} onClick={() => updateParam('op', o)} className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${op === o ? 'bg-white shadow-sm text-habita' : 'text-gray-600'}`} data-testid={`op-${o}-btn`}>
                {o === 'venta' ? 'Comprar' : 'Arrendar'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
            <MapPin className="w-4 h-4 text-habita" />
            <input value={q} onChange={(e) => updateParam('q', e.target.value)} placeholder="Ciudad o barrio" className="bg-transparent outline-none text-sm w-40" data-testid="search-q-input" />
            {q && <button onClick={() => updateParam('q', '')}><X className="w-3 h-3 text-gray-500" /></button>}
          </div>

          <div className="relative">
            <select value={type} onChange={(e) => updateParam('type', e.target.value)} className="appearance-none bg-gray-100 rounded-full pl-4 pr-9 h-10 text-sm font-medium outline-none cursor-pointer">
              <option value="">Todos los tipos</option>
              {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-500" />
          </div>

          <div className="relative">
            <select value={beds} onChange={(e) => updateParam('beds', e.target.value)} className="appearance-none bg-gray-100 rounded-full pl-4 pr-9 h-10 text-sm font-medium outline-none cursor-pointer">
              <option value="0">Habitaciones</option>
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}+ hab</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-500" />
          </div>

          <button onClick={() => setShowFilters(!showFilters)} className="px-4 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-sm font-medium flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" /> Más filtros
          </button>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="appearance-none bg-white border border-gray-200 rounded-full pl-4 pr-9 h-10 text-sm font-medium outline-none cursor-pointer">
                <option value="reciente">Más recientes</option>
                <option value="precio-asc">Precio: menor a mayor</option>
                <option value="precio-desc">Precio: mayor a menor</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-500" />
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="max-w-7xl mx-auto px-4 lg:px-8 pb-5 grid md:grid-cols-2 gap-6 border-t border-gray-100 pt-5">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-gray-700">Rango de precio</label>
                <span className="text-xs text-gray-500">{formatCOP(minPrice)} - {formatCOP(maxPrice)}</span>
              </div>
              <Slider
                value={[minPrice, maxPrice]}
                min={priceRange[0]}
                max={priceRange[1]}
                step={op === 'venta' ? 10000000 : 100000}
                onValueChange={(v) => {
                  const np = new URLSearchParams(params);
                  np.set('minPrice', v[0]);
                  np.set('maxPrice', v[1]);
                  setParams(np);
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* MOBILE STICKY TOP BAR */}
      <div className="md:hidden sticky top-16 z-30 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-2">
        <div className="flex items-center bg-gray-100 rounded-full p-1 shrink-0">
          {['venta', 'arriendo'].map(o => (
            <button
              key={o}
              onClick={() => updateParam('op', o)}
              className={`px-3 h-8 rounded-full text-xs font-semibold transition-colors ${op === o ? 'bg-white shadow-sm text-habita' : 'text-gray-600'}`}
              data-testid={`op-${o}-mobile-btn`}
            >
              {o === 'venta' ? 'Comprar' : 'Arrendar'}
            </button>
          ))}
        </div>
        <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full min-w-0">
          <MapPin className="w-3.5 h-3.5 text-habita shrink-0" />
          <input
            value={q}
            onChange={(e) => updateParam('q', e.target.value)}
            placeholder="Ciudad o barrio"
            className="bg-transparent outline-none text-xs flex-1 min-w-0"
            data-testid="search-q-mobile-input"
          />
        </div>
        <button
          onClick={() => setShowFilters(true)}
          className="relative shrink-0 w-9 h-9 rounded-full bg-habita text-white flex items-center justify-center"
          data-testid="open-mobile-filters-btn"
          aria-label="Abrir filtros"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {activeFilters.length > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">
              {activeFilters.length}
            </span>
          )}
        </button>
      </div>

      {/* Active filter chips (mobile) */}
      {activeFilters.length > 0 && (
        <div className="md:hidden px-4 py-3 flex flex-wrap gap-2 border-b border-gray-100">
          {activeFilters.map((f) => (
            <span
              key={f.key}
              className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-habita-mint text-habita text-xs font-semibold"
            >
              {f.label}
              <button
                onClick={() => f.key === 'price' ? clearPrice() : updateParam(f.key, '')}
                className="hover:text-habita-dark"
                aria-label="Quitar filtro"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* MOBILE FILTERS BOTTOM SHEET */}
      {showFilters && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/55 backdrop-blur-sm" onClick={() => setShowFilters(false)} data-testid="mobile-filters-overlay">
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white px-5 pt-5 pb-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-serif text-xl font-bold text-habita-dark">Filtros</h3>
                <p className="text-xs text-gray-500">{total} resultados</p>
              </div>
              <button onClick={() => setShowFilters(false)} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center" aria-label="Cerrar">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-6 pb-24">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Tipo de propiedad</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => updateParam('type', '')} className={`px-4 h-9 rounded-full text-sm font-medium border ${!type ? 'bg-habita text-white border-habita' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                    Todos
                  </button>
                  {PROPERTY_TYPES.map(t => (
                    <button
                      key={t.value}
                      onClick={() => updateParam('type', t.value)}
                      className={`px-4 h-9 rounded-full text-sm font-medium border ${type === t.value ? 'bg-habita text-white border-habita' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Habitaciones</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => updateParam('beds', '0')} className={`px-4 h-9 rounded-full text-sm font-medium border ${!beds ? 'bg-habita text-white border-habita' : 'border-gray-200 text-gray-700'}`}>
                    Cualquiera
                  </button>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      onClick={() => updateParam('beds', n)}
                      className={`px-4 h-9 rounded-full text-sm font-medium border ${beds === n ? 'bg-habita text-white border-habita' : 'border-gray-200 text-gray-700'}`}
                    >
                      {n}+
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Rango de precio</label>
                  <span className="text-xs text-gray-500">{formatCOP(minPrice)} - {formatCOP(maxPrice)}</span>
                </div>
                <Slider
                  value={[minPrice, maxPrice]}
                  min={priceRange[0]}
                  max={priceRange[1]}
                  step={op === 'venta' ? 10000000 : 100000}
                  onValueChange={(v) => {
                    const np = new URLSearchParams(params);
                    np.set('minPrice', v[0]);
                    np.set('maxPrice', v[1]);
                    setParams(np);
                  }}
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Ordenar por</label>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {[
                    { value: 'reciente', label: 'Más recientes' },
                    { value: 'precio-asc', label: 'Precio: menor a mayor' },
                    { value: 'precio-desc', label: 'Precio: mayor a menor' },
                  ].map(s => (
                    <button
                      key={s.value}
                      onClick={() => setSortBy(s.value)}
                      className={`px-4 h-11 rounded-xl text-sm font-medium text-left border ${sortBy === s.value ? 'bg-habita-mint text-habita border-habita' : 'border-gray-200 text-gray-700'}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setParams(new URLSearchParams({ op })); setSortBy('reciente'); }}
                className="flex-1 h-12 rounded-full"
              >
                Limpiar
              </Button>
              <Button
                onClick={() => setShowFilters(false)}
                className="btn-habita flex-1 h-12 rounded-full"
                data-testid="apply-mobile-filters-btn"
              >
                Ver {total} resultados
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SPLIT VIEW: list (left) + map (right) on desktop, toggle on mobile */}
      <div className="max-w-[1600px] mx-auto px-0 lg:px-4 py-0 lg:py-4">
        <div className="lg:flex lg:gap-4 lg:items-stretch" style={{ height: 'calc(100vh - 180px)' }}>
          {/* === LEFT: LIST === */}
          <div
            className={`flex-1 lg:overflow-y-auto lg:pr-1 ${mobileView === 'map' ? 'hidden lg:block' : 'block'}`}
            data-testid="search-list-pane"
          >
            <div className="px-4 lg:px-2 pt-4 pb-3 lg:sticky lg:top-0 bg-white z-10">
              <h1 className="font-serif text-xl md:text-2xl font-bold text-habita-dark">
                {loading ? 'Buscando...' : `${total} propiedad${total !== 1 ? 'es' : ''} ${op === 'venta' ? 'en venta' : 'en arriendo'}${q ? ` en ${q}` : ' en Colombia'}`}
              </h1>
              <p className="text-xs text-gray-500 mt-1">
                {bbox
                  ? 'Mostrando solo propiedades en el área visible del mapa'
                  : 'Inventario verificado · Actualizado hace pocos minutos'}
              </p>
            </div>

            <div className="px-4 lg:px-2 pb-8">
              {loading ? (
                <PropertyCardSkeletonGrid count={6} />
              ) : items.length === 0 ? (
                <div className="text-center py-14 bg-white rounded-3xl border border-gray-100" data-testid="search-empty-state">
                  <div className="w-14 h-14 rounded-full bg-habita-mint mx-auto flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-habita" />
                  </div>
                  <h2 className="font-serif text-2xl font-bold text-habita-dark mt-5">
                    No es un no — es un &ldquo;probemos con otra cosa&rdquo;.
                  </h2>
                  <p className="text-gray-600 mt-3 max-w-md mx-auto text-sm">
                    No encontramos propiedades con esos filtros ahora mismo. Ampliar zona o presupuesto suele resolverlo.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    {bbox && (
                      <Button onClick={() => setBbox(null)} variant="outline" className="rounded-full h-11 px-5" data-testid="search-empty-clear-bbox">
                        Quitar filtro de mapa
                      </Button>
                    )}
                    <Button onClick={() => setParams(new URLSearchParams({ op }))} variant="outline" className="rounded-full h-11 px-5" data-testid="search-empty-clear-filters">
                      Limpiar filtros
                    </Button>
                    <Button onClick={() => navigate('/advisor/inicio')} className="btn-habita rounded-full h-11 px-5" data-testid="search-empty-open-advisor">
                      Que el Advisor me guíe
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  <div className="md:col-span-2">
                    <AdvisorSearchLens
                      totalItems={total}
                      onRecommendationsResolved={setAdvisorReasons}
                    />
                  </div>
                  {items.map((p) => (
                    <div
                      key={p.id}
                      onMouseEnter={() => setHoveredId(p.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      className={`relative transition-shadow ${hoveredId === p.id ? 'ring-2 ring-habita rounded-2xl' : ''} ${compareIds.includes(p.id) ? 'ring-2 ring-habita rounded-2xl' : ''}`}
                    >
                      <label
                        className={`absolute top-2 left-2 z-10 bg-white/95 backdrop-blur rounded-full px-2.5 py-1 text-[11px] font-semibold cursor-pointer flex items-center gap-1.5 border transition ${compareIds.includes(p.id) ? 'border-habita text-habita' : 'border-gray-200 text-gray-600 hover:border-habita'}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="w-3 h-3 accent-habita"
                          checked={compareIds.includes(p.id)}
                          onChange={() => toggleCompare(p.id)}
                          data-testid={`compare-check-${p.id}`}
                        />
                        Comparar
                      </label>
                      <PropertyCard p={p} advisorReason={advisorReasons[p.id]} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {compareIds.length >= 2 && (
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40" data-testid="compare-fab">
              <Link
                to={`/comparar?ids=${compareIds.join(',')}`}
                className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-habita text-white font-semibold text-sm shadow-lg hover:opacity-90 transition"
              >
                Comparar {compareIds.length} propiedades →
              </Link>
              <button
                onClick={() => setCompareIds([])}
                className="ml-2 h-12 px-3 rounded-full bg-white border border-gray-200 text-gray-500 text-sm hover:text-habita"
                aria-label="Limpiar selección"
              >×</button>
            </div>
          )}

          {/* === RIGHT: MAP === */}
          <div
            className={`lg:w-[55%] lg:max-w-[860px] lg:relative lg:rounded-2xl lg:overflow-hidden lg:shadow-sm border-l lg:border border-gray-200 ${mobileView === 'map' ? 'block fixed inset-0 top-32 z-30' : 'hidden lg:block'}`}
            data-testid="search-map-pane"
          >
            <MapSearchView
              items={items}
              loading={loading}
              hoveredId={hoveredId}
              onHoverProperty={setHoveredId}
              onBoundsChange={onMapBoundsConfirmed}
              visible={mobileView === 'map'}
              className="w-full h-full"
            />
          </div>
        </div>
      </div>

      {/* Mobile toggle: list ↔ map (FAB) */}
      <button
        onClick={() => setMobileView(v => v === 'list' ? 'map' : 'list')}
        className="lg:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-40 bg-habita-dark text-white px-5 h-12 rounded-full shadow-2xl flex items-center gap-2 text-sm font-semibold"
        data-testid="mobile-view-toggle"
        aria-label={mobileView === 'list' ? 'Ver mapa' : 'Ver lista'}
      >
        {mobileView === 'list' ? <><MapIcon className="w-4 h-4" /> Ver mapa</> : <><ListIconLucide className="w-4 h-4" /> Ver lista</>}
      </button>

      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
}
